import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import calendar from '~/assets/icons/calendar.svg';
import { CurrencyIcon } from '~/components/collections';
import { ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import { rage, useObservable } from '~/rage';
import { Currency, Gender, IStats, WalletType } from '~/shared';
import { StatsCopyButton } from '../StatsCopyButton';
import { formatPlayTimeSeconds } from './StatsMain.model';
import {
	StyledButton,
	StyledButtonsContainer,
	StyledCard,
	StyledCardBottom,
	StyledContainer,
	StyledDateContainer,
	StyledMoneyContainer,
	StyledTime,
} from './StatsMain.styled';

const StatsMain = () => {
	// const premiumActive = useObservable(rage.player.getPremium());
	// const premiumDays = useObservable(rage.player.getPremiumDays());
	const playerGender = useObservable(rage.player.getPlayerGender());
	const playerLevel = useObservable(rage.player.getPlayerLevel());
	const level = playerLevel?.level ?? 0;
	const exp = playerLevel?.exp ?? 0;
	const nextLevelExp = playerLevel?.nextLevelExp ?? 0;
	const rubleIcon = useWalletIcon(Currency.Ruble, true);
	const playTimeStats = useObservable(rage.stats.getPlayTimeStats());
	const moneySpentStats = useObservable(rage.stats.getMoneySpentStats());
	const moneyEarnedStats = useObservable(rage.stats.getMoneyEarnedStats());
	const accountId = useObservable(rage.player.getAccountId());
	const phoneNumber = useObservable(rage.phone.getPhoneNumber());
	const cashBalance = useObservable(rage.bank.getBalance(WalletType.Cash));
	const bankBalance = useObservable(rage.bank.getBalance(WalletType.Bank));
	const registrationDate = useObservable(
		rage.player.getPlayerRegistrationDate(),
	);

	const [timeSpendSelected, setTimeSpendSelected] =
		useState<keyof IStats>('daily');
	const [moneySpendSelected, setMoneySpendSelected] =
		useState<keyof IStats>('daily');
	const [moneyEarnedSelected, setMoneyEarnedSelected] =
		useState<keyof IStats>('daily');

	const [days, hours, minutes] = useMemo(
		() => formatPlayTimeSeconds(playTimeStats[timeSpendSelected]),
		[playTimeStats, timeSpendSelected],
	);

	useEffect(() => {
		rage.stats
			.fetchStats()
			.catch((err) => rage.notifications.showException(err));
	}, []);

	const handleCopyId = useCallback(() => {
		if (accountId) {
			rage.input.copyToClipboard(accountId);
			rage.notifications.showInfo('UID скопирован в буфер обмена');
		}
	}, [accountId]);

	const handleCopyPhoneNumber = useCallback(() => {
		if (phoneNumber) {
			rage.input.copyToClipboard(phoneNumber);
			rage.notifications.showInfo('Номер телефона скопирован в буфер обмена');
		}
	}, [phoneNumber]);

	return (
		<StyledContainer tabIndex={-1}>
			{/* <StyledPremiumCard $active={premiumActive} $span={2}>
				<Text className={'title'}>Премиум</Text>
				<Text style={{ fontSize: 26 }}>
					{premiumActive ? (
						<Fragment>
							Активен <span>({premiumDays} дней)</span>
						</Fragment>
					) : (
						'Не активен'
					)}
				</Text>
			</StyledPremiumCard> */}
			<StyledCard $span={3}>
				<Text className={'title'}>Уровень</Text>
				<Text style={{ fontSize: 26 }}>{level} Уровень</Text>
			</StyledCard>
			<StyledCard $disabled>
				<Text className={'title'}>Опыт</Text>
				<Text style={{ fontSize: 26 }}>
					{exp.toLocaleString('ru-RU')}{' '}
					<span style={{ fontSize: 20, opacity: 0.5 }}>
						/ {nextLevelExp.toLocaleString('ru-RU')}
					</span>
				</Text>
			</StyledCard>
			<StyledCard $span={3}>
				<Text className={'title'}>Пол</Text>
				<Text style={{ fontSize: 26 }}>
					{playerGender === Gender.MALE ? 'Мужской' : 'Женский'}
				</Text>
			</StyledCard>
			<StyledCard $span={3}>
				<Text className={'title'}>Проведено времени в игре</Text>
				<StyledCardBottom>
					<StyledTime style={{ fontSize: 26 }}>
						{days ? (
							<Fragment>
								{days} <span>д.</span>
							</Fragment>
						) : null}{' '}
						{hours ? (
							<Fragment>
								{hours} <span>ч.</span>
							</Fragment>
						) : null}{' '}
						<Fragment>
							{minutes} <span>м.</span>
						</Fragment>
					</StyledTime>
					<StyledButtonsContainer>
						<StyledButton
							color={ButtonColors.Default}
							height={ButtonHeight.Height30}
							$active={timeSpendSelected === 'daily'}
							onClick={() => setTimeSpendSelected('daily')}
						>
							<Text>День</Text>
						</StyledButton>
						<StyledButton
							color={ButtonColors.Default}
							height={ButtonHeight.Height30}
							$active={timeSpendSelected === 'monthly'}
							onClick={() => setTimeSpendSelected('monthly')}
						>
							<Text>Месяц</Text>
						</StyledButton>
						<StyledButton
							color={ButtonColors.Default}
							height={ButtonHeight.Height30}
							$active={timeSpendSelected === 'yearly'}
							onClick={() => setTimeSpendSelected('yearly')}
						>
							<Text>Год</Text>
						</StyledButton>
						<StyledButton
							color={ButtonColors.Default}
							height={ButtonHeight.Height30}
							$active={timeSpendSelected === 'total'}
							onClick={() => setTimeSpendSelected('total')}
						>
							<Text>За все время</Text>
						</StyledButton>
					</StyledButtonsContainer>
				</StyledCardBottom>
			</StyledCard>
			<StyledCard $span={3}>
				<Text className={'title'}>Потрачено денег</Text>
				<StyledCardBottom>
					<StyledMoneyContainer>
						<Text>
							{moneySpentStats[moneySpendSelected].toLocaleString('ru-RU')}
						</Text>
						<CurrencyIcon icon={rubleIcon} size="lg" />
					</StyledMoneyContainer>
					<StyledButtonsContainer>
						<StyledButton
							color={ButtonColors.Default}
							height={ButtonHeight.Height30}
							$active={moneySpendSelected === 'daily'}
							onClick={() => setMoneySpendSelected('daily')}
						>
							<Text>День</Text>
						</StyledButton>
						<StyledButton
							color={ButtonColors.Default}
							height={ButtonHeight.Height30}
							$active={moneySpendSelected === 'monthly'}
							onClick={() => setMoneySpendSelected('monthly')}
						>
							<Text>Месяц</Text>
						</StyledButton>
						<StyledButton
							color={ButtonColors.Default}
							height={ButtonHeight.Height30}
							$active={moneySpendSelected === 'yearly'}
							onClick={() => setMoneySpendSelected('yearly')}
						>
							<Text>Год</Text>
						</StyledButton>
						<StyledButton
							color={ButtonColors.Default}
							height={ButtonHeight.Height30}
							$active={moneySpendSelected === 'total'}
							onClick={() => setMoneySpendSelected('total')}
						>
							<Text>За все время</Text>
						</StyledButton>
					</StyledButtonsContainer>
				</StyledCardBottom>
			</StyledCard>
			<StyledCard $span={2}>
				<Text className={'title'}>Ваш UID</Text>
				<StyledCardBottom>
					<Text style={{ fontSize: 26 }}>#{accountId}</Text>
					<StatsCopyButton onClick={handleCopyId} />
				</StyledCardBottom>
			</StyledCard>
			<StyledCard $disabled>
				<Text className={'title'}>Номер телефона</Text>
				<StyledCardBottom>
					<Text style={{ fontSize: 26 }}>{phoneNumber}</Text>
					<StatsCopyButton onClick={handleCopyPhoneNumber} />
				</StyledCardBottom>
			</StyledCard>
			<StyledCard $span={2}>
				<Text className={'title'}>Денег на руках</Text>
				<StyledMoneyContainer>
					<Text>{cashBalance.toLocaleString('ru-RU')}</Text>
					<CurrencyIcon icon={rubleIcon} size="lg" />
				</StyledMoneyContainer>
			</StyledCard>
			<StyledCard $span={2}>
				<Text className={'title'}>Денег в банке</Text>
				<StyledMoneyContainer>
					<Text>{bankBalance.toLocaleString('ru-RU')}</Text>
					<CurrencyIcon icon={rubleIcon} size="lg" />
				</StyledMoneyContainer>
			</StyledCard>
			<StyledCard $span={3}>
				<Text className={'title'}>Заработано денег</Text>
				<StyledCardBottom>
					<StyledMoneyContainer>
						<Text>
							{moneyEarnedStats[moneyEarnedSelected].toLocaleString('ru-RU')}
						</Text>
						<CurrencyIcon icon={rubleIcon} size="lg" />
					</StyledMoneyContainer>
					<StyledButtonsContainer>
						<StyledButton
							color={ButtonColors.Default}
							height={ButtonHeight.Height30}
							$active={moneyEarnedSelected === 'daily'}
							onClick={() => setMoneyEarnedSelected('daily')}
						>
							<Text>День</Text>
						</StyledButton>
						<StyledButton
							color={ButtonColors.Default}
							height={ButtonHeight.Height30}
							$active={moneyEarnedSelected === 'monthly'}
							onClick={() => setMoneyEarnedSelected('monthly')}
						>
							<Text>Месяц</Text>
						</StyledButton>
						<StyledButton
							color={ButtonColors.Default}
							height={ButtonHeight.Height30}
							$active={moneyEarnedSelected === 'yearly'}
							onClick={() => setMoneyEarnedSelected('yearly')}
						>
							<Text>Год</Text>
						</StyledButton>
						<StyledButton
							color={ButtonColors.Default}
							height={ButtonHeight.Height30}
							$active={moneyEarnedSelected === 'total'}
							onClick={() => setMoneyEarnedSelected('total')}
						>
							<Text>За все время</Text>
						</StyledButton>
					</StyledButtonsContainer>
				</StyledCardBottom>
			</StyledCard>
			<StyledCard $span={3}>
				<Text className={'title'}>Дата регистрации</Text>
				<StyledDateContainer>
					<img src={calendar} />
					<Text>
						{new Date(registrationDate).toLocaleDateString('ru-RU', {
							day: '2-digit',
							month: '2-digit',
							year: '2-digit',
							timeZone: 'Europe/Moscow',
						})}
					</Text>
				</StyledDateContainer>
			</StyledCard>
		</StyledContainer>
	);
};

export { StatsMain };
